import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PageShell } from '../../layout/Shell';
import { getReplay, parseReplayUrl } from '../../shared/api/client';
import { replayFromApi } from '../../shared/api/transform';
import {
  AltitudeChart,
  ChartFrame,
  GearChart,
  InputHistogram,
  PYRChart,
  RPMChart,
  SpeedChart,
  SteerChart,
  SyncContext,
  ThrottleBrakeChart,
  VelVsSpeedChart,
} from '../../shared/design/Charts';
import { Icon } from '../../shared/design/Icons';
import { Minimap } from '../../shared/design/Minimap';
import { Button, Chip, EmptyState, ParseProgress, Stat, TierBadge } from '../../shared/design/Primitives';
import { Scrubber } from '../../shared/design/Scrubber';
import { useAsync } from '../../shared/hooks/useAsync';
import { formatTime, Replay, sampleAt, Tick } from '../../shared/model/Replay';

export function ScanPage() {
  const { uid } = useParams<{ uid: string }>();
  const [search] = useSearchParams();
  const url = search.get('url');

  // Two entry modes: fetch a stored replay by ghostUid, or parse a fresh
  // GBX URL (which the backend auto-saves on the way through).
  const state = useAsync(async () => {
    if (uid) return replayFromApi(await getReplay(uid));
    if (url) return replayFromApi(await parseReplayUrl(url));
    return null;
  }, [uid, url]);

  if (!uid && !url) {
    return (
      <PageShell active="scan">
        <div className="tm" style={{ padding: 60, maxWidth: 720, margin: '0 auto' }}>
          <h1 className="h1" style={{ marginBottom: 12 }}>Scan a replay</h1>
          <p className="p" style={{ marginBottom: 24 }}>
            Start from the Home page — paste a replay URL there and we'll bring you back here with the analysis.
          </p>
          <EmptyState
            title="No replay selected"
            body="Open a replay from the Leaderboard, or paste a URL on the Home page."
            icon={<Icon.Upload />}
          />
        </div>
      </PageShell>
    );
  }

  if (state.loading) {
    return (
      <PageShell active="scan">
        <div className="tm" style={{ padding: 60, maxWidth: 520, margin: '0 auto' }}>
          <ParseProgress
            steps={[
              { label: url ? `Downloading ${url.slice(0, 60)}…` : 'Loading replay from store', done: false, active: true },
              { label: 'Reading GBX headers', done: false },
              { label: 'Extracting input ticks', done: false },
              { label: 'Computing verdict', done: false },
            ]}
          />
        </div>
      </PageShell>
    );
  }

  if (state.error || !state.data) {
    return (
      <PageShell active="scan">
        <div className="tm" style={{ padding: 60, maxWidth: 720, margin: '0 auto' }}>
          <EmptyState
            title="Couldn't load that replay"
            body={state.error?.message ?? 'Unknown error'}
            icon={<Icon.Warn />}
            accent="var(--bad)"
          />
        </div>
      </PageShell>
    );
  }

  return <ScanWorkspace replay={state.data} />;
}

function ScanWorkspace({ replay }: { replay: Replay }) {
  const [scrubT, setScrubT] = useState(0.34);
  const [hoverT, setHoverT] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [overlay, setOverlay] = useState<'speed' | 'gear' | 'steer' | 'gas'>('speed');
  const [kmh] = useState(true);
  const [expandedCharts, setExpandedCharts] = useState({ pyr: false, alt: false, velvs: false });

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const start = performance.now();
    const startT = scrubT;
    const loop = (t: number) => {
      const dt = (t - start) / 1000;
      const next = startT + (dt / Math.max(0.001, replay.data.duration)) * speed;
      if (next >= 1) {
        setScrubT(1);
        setPlaying(false);
        return;
      }
      setScrubT(next);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, speed]);

  const cur = useMemo(() => sampleAt(replay, scrubT), [replay, scrubT]);
  const meta = replay.meta;

  return (
    <SyncContext.Provider value={{ scrubT, hoverT, setHoverT }}>
      <PageShell active="scan" topBarPlaceholder="Paste replay URL or upload .Replay.gbx">
        <div className="tm" style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
          <SummaryBar replay={replay} />

          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
            <div
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-8)',
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="eyebrow">Track · top-down</div>
                <div style={{ flex: 1 }} />
                <div
                  style={{
                    display: 'flex',
                    gap: 4,
                    background: 'var(--bg-surface-2)',
                    padding: 3,
                    borderRadius: 'var(--r-6)',
                  }}
                >
                  {(['speed', 'gear', 'steer', 'gas'] as const).map((o) => (
                    <button
                      key={o}
                      onClick={() => setOverlay(o)}
                      style={{
                        padding: '4px 10px',
                        background: overlay === o ? 'var(--bg-surface-3)' : 'transparent',
                        color: overlay === o ? 'var(--fg-1)' : 'var(--fg-3)',
                        border: 'none',
                        borderRadius: 4,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        fontWeight: 500,
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                      }}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ height: 360, position: 'relative' }}>
                <Minimap replay={replay} width={780} height={360} scrubT={scrubT} overlay={overlay} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-muted)' }}>0</span>
                <div
                  style={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    background:
                      'linear-gradient(to right, #1a4e8a, #0099ff, #00d4c4, #7bf06b, #ffe14a, #ff8a3c, #ff3355)',
                  }}
                />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-muted)' }}>
                  {meta.topSpeedKmh} km/h
                </span>
              </div>
            </div>

            <LiveReadout cur={cur} kmh={kmh} />
          </div>

          <div style={{ marginTop: 12 }}>
            <Scrubber
              replay={replay}
              scrubT={scrubT}
              onChange={setScrubT}
              playing={playing}
              setPlaying={setPlaying}
              speed={speed}
              setSpeed={setSpeed}
            />
          </div>

          <div style={{ marginTop: 20 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <h2 className="h2" style={{ color: 'var(--fg-1)' }}>Telemetry</h2>
              <div style={{ display: 'flex', gap: 6 }}>
                <Chip>km/h</Chip>
                <Chip>m/s</Chip>
                <Chip icon={<Icon.Settings />}>Configure</Chip>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SpeedChart replay={replay} kmh={kmh} />
              <SteerChart replay={replay} />
              <ThrottleBrakeChart replay={replay} />
              <GearChart replay={replay} />
              <RPMChart replay={replay} />
              <ChartFrame
                title="Pitch · Yaw · Roll"
                accent="#b56bff"
                height={110}
                collapsed={!expandedCharts.pyr}
                onToggle={() => setExpandedCharts((s) => ({ ...s, pyr: !s.pyr }))}
                unit="rad"
              >
                {expandedCharts.pyr ? <PYRChart replay={replay} /> : null}
              </ChartFrame>
              <ChartFrame
                title="Altitude"
                accent="#5fb5ff"
                height={90}
                unit="m"
                collapsed={!expandedCharts.alt}
                onToggle={() => setExpandedCharts((s) => ({ ...s, alt: !s.alt }))}
              >
                {expandedCharts.alt && <AltitudeChart replay={replay} />}
              </ChartFrame>
              <ChartFrame
                title="Velocity vs Speed"
                accent="#00d4c4"
                height={110}
                unit="m/s"
                collapsed={!expandedCharts.velvs}
                onToggle={() => setExpandedCharts((s) => ({ ...s, velvs: !s.velvs }))}
              >
                {expandedCharts.velvs && <VelVsSpeedChart replay={replay} />}
              </ChartFrame>
              <InputHistogram replay={replay} />
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <VerdictPanel replay={replay} />
          </div>

          <div style={{ height: 80 }} />
        </div>
      </PageShell>
    </SyncContext.Provider>
  );
}

// ============ Summary bar ============
function SummaryBar({ replay }: { replay: Replay }) {
  const meta = replay.meta;
  const data = replay.data;
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        padding: '18px 22px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <TierBadge tier={meta.tier} size="xl" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div className="eyebrow">{meta.location || '—'}</div>
            <span style={{ color: 'var(--fg-dim)' }}>·</span>
            <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>{meta.ghostUid}</span>
            <button
              style={{
                padding: '2px 4px',
                background: 'transparent',
                border: 'none',
                color: 'var(--fg-muted)',
                cursor: 'pointer',
                display: 'inline-flex',
              }}
              onClick={() => navigator.clipboard?.writeText(meta.ghostUid)}
            >
              <Icon.Copy />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--fg-1)', margin: 0, letterSpacing: '-0.01em' }}>
              {meta.author}
            </h1>
            <span style={{ fontSize: 18, color: 'var(--fg-3)' }}>on</span>
            <span style={{ fontSize: 18, color: 'var(--fg-1)', fontWeight: 500 }}>{meta.map}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="eyebrow" style={{ marginBottom: 2 }}>Finish</div>
          <div
            className="num"
            style={{
              fontSize: 36,
              fontWeight: 600,
              color: 'var(--fg-1)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '-0.02em',
            }}
          >
            {meta.raceTimeStr}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Button variant="primary" size="sm" icon={<Icon.Compare />}>Compare</Button>
          <Button variant="secondary" size="sm" icon={<Icon.Share />}>Share</Button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 0,
          marginTop: 18,
          paddingTop: 14,
          borderTop: '1px solid var(--border-subtle)',
        }}
      >
        <Stat label="Respawns" value={meta.respawns} />
        <Stat
          label="Top speed"
          value={meta.topSpeedKmh}
          unit="km/h"
          accent={data.topSpeed * 3.6 > 340 ? 'var(--ok)' : undefined}
        />
        <Stat label="Avg speed" value={meta.avgSpeedKmh} unit="km/h" />
        <Stat label="Checkpoints" value={`${data.checkpoints.length} / ${data.checkpoints.length}`} />
        <Stat
          label="Sus. clusters"
          value={data.clusters.length}
          accent={data.clusters.length > 4 ? 'var(--bad)' : data.clusters.length > 1 ? 'var(--warn)' : 'var(--ok)'}
        />
        <Stat
          label="Challenge UID"
          value={meta.challengeUid ? meta.challengeUid.slice(-10) : '—'}
          mono
        />
      </div>
    </div>
  );
}

// ============ Live readout ============
function LiveReadout({ cur, kmh = true }: { cur: Tick; kmh?: boolean }) {
  const tStr = formatTime(cur.t * 1000);
  const spd = kmh ? cur.speed * 3.6 : cur.speed;

  const Row = ({ label, value, unit, color = 'var(--fg-1)' }: { label: ReactNode; value: ReactNode; unit?: ReactNode; color?: string }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '8px 0',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <span className="eyebrow">{label}</span>
      <span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 14,
            fontWeight: 600,
            color,
            letterSpacing: '-0.01em',
          }}
        >
          {value}
        </span>
        {unit && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-muted)', marginLeft: 4 }}>
            {unit}
          </span>
        )}
      </span>
    </div>
  );

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        padding: '14px 18px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div className="eyebrow">Live tick @ {tStr}</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--accent)',
              boxShadow: 'var(--accent-glow)',
            }}
          />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-3)' }}>SCRUB</span>
        </div>
      </div>
      <Row label="Speed" value={spd.toFixed(1)} unit={kmh ? 'km/h' : 'm/s'} color="var(--accent)" />
      <Row label="Gear" value={`G${cur.gear}`} color="#ffe14a" />
      <Row label="RPM" value={cur.rpm.toFixed(0)} unit="rev/min" color="#ff8a3c" />
      <Row
        label="Steer"
        value={`${cur.steer >= 0 ? '+' : ''}${cur.steer.toFixed(3)}`}
        color={Math.abs(cur.steer) > 0.05 ? (cur.steer > 0 ? '#ff8a3c' : '#0099ff') : 'var(--fg-1)'}
      />
      <Row
        label="Throttle"
        value={cur.brake ? '— —' : cur.gas ? 'GAS' : '— —'}
        color={cur.brake ? 'var(--bad)' : cur.gas ? 'var(--ok)' : 'var(--fg-muted)'}
      />
      <Row label="Position" value={`${cur.x.toFixed(0)}, ${cur.y.toFixed(0)}, ${cur.z.toFixed(0)}`} color="var(--fg-2)" />
      <Row label="Velocity" value={`|${cur.vmag.toFixed(1)}|`} unit="m/s" color="var(--fg-2)" />
      <Row
        label="Pitch / Yaw / Roll"
        value={`${cur.pitch.toFixed(2)} ${cur.yaw.toFixed(2)} ${cur.roll.toFixed(2)}`}
        color="var(--fg-2)"
      />
    </div>
  );
}

export { LiveReadout };

// ============ Verdict ============
function VerdictPanel({ replay }: { replay: Replay }) {
  const data = replay.data;
  const meta = replay.meta;
  // The backend's tier is the source of truth — clusters here are already
  // pre-filtered to "custom action-key" runs, so any non-S tier means we
  // found at least one suspicious cluster.
  const isBad = meta.tier !== 'S';
  const topCluster = [...data.clusters].sort((a, b) => b.count - a.count)[0];

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: `1px solid ${isBad ? 'var(--bad)' : 'var(--ok)'}`,
        borderRadius: 'var(--r-8)',
        padding: 22,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
        <TierBadge tier={meta.tier} size="xl" />
        <div style={{ flex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Verdict</div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: isBad ? 'var(--bad)' : 'var(--ok)',
              margin: 0,
              marginBottom: 8,
              letterSpacing: '-0.01em',
            }}
          >
            {isBad
              ? `Action-key input detected — ${data.clusters.length} clusters`
              : 'Clean analog input throughout — no suspicious clusters'}
          </h2>
          {isBad && topCluster && (
            <p className="p" style={{ marginBottom: 12, color: 'var(--fg-2)' }}>
              We saw{' '}
              <span className="mono" style={{ color: 'var(--bad)', fontWeight: 600 }}>
                {topCluster.count}
              </span>{' '}
              identical steering inputs of value{' '}
              <span className="mono" style={{ color: 'var(--bad)', fontWeight: 600 }}>
                {topCluster.value > 0 ? '+' : ''}
                {topCluster.value.toFixed(3)}
              </span>{' '}
              clustered between{' '}
              <span className="mono" style={{ color: 'var(--fg-1)', fontWeight: 600 }}>
                {formatTime(topCluster.tStart * 1000)}
              </span>{' '}
              and{' '}
              <span className="mono" style={{ color: 'var(--fg-1)', fontWeight: 600 }}>
                {formatTime(topCluster.tEnd * 1000)}
              </span>
              . That's not analog stick behavior — an analog axis varies continuously sample-to-sample.
            </p>
          )}
          {!isBad && (
            <p className="p" style={{ marginBottom: 12, color: 'var(--fg-2)' }}>
              No steering input repeats exactly for &gt; 8 consecutive ticks. Distribution is continuous, consistent with an analog
              stick or wheel.
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="eyebrow">Confidence</span>
            <div
              style={{
                flex: 1,
                maxWidth: 280,
                height: 6,
                background: 'var(--bg-surface-2)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: isBad ? '92%' : '88%',
                  background: isBad ? 'var(--bad)' : 'var(--ok)',
                }}
              />
            </div>
            <span className="num" style={{ fontSize: 13, color: isBad ? 'var(--bad)' : 'var(--ok)', fontWeight: 600 }}>
              {isBad ? '92%' : '88%'}
            </span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--border-subtle)' }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>
          Top exact-value bins
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {data.exactBins.slice(0, 5).map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  width: 80,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: b.isSuspicious ? 'var(--bad)' : 'var(--fg-1)',
                }}
              >
                {b.value > 0 ? '+' : ''}
                {b.value.toFixed(3)}
              </span>
              <div
                style={{
                  flex: 1,
                  height: 6,
                  background: 'var(--bg-surface-2)',
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${(b.count / (data.exactBins[0]?.count || 1)) * 100}%`,
                    background: b.isSuspicious ? 'var(--bad)' : 'var(--fg-2)',
                  }}
                />
              </div>
              <span className="num" style={{ width: 50, textAlign: 'right', fontSize: 12, color: 'var(--fg-2)' }}>
                ×{b.count}
              </span>
              {b.isSuspicious && (
                <span
                  style={{
                    fontSize: 10,
                    color: 'var(--bad)',
                    fontFamily: 'var(--font-mono)',
                    padding: '2px 6px',
                    background: 'var(--bad-12)',
                    borderRadius: 3,
                  }}
                >
                  SUSPECT
                </span>
              )}
            </div>
          ))}
          {data.exactBins.length === 0 && (
            <div className="p" style={{ fontSize: 12, color: 'var(--fg-muted)' }}>No steering data available.</div>
          )}
        </div>
      </div>
    </div>
  );
}
